import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import http from 'helpers/requests';
import { apiErrorMessage } from 'helpers/serverIssues';
import { cgToast, notify, AlertType } from 'modules/Common/ui/toast';
import { useCnGoods } from 'modules/Common/utils/Utility';
import { useRegimeProfile } from 'modules/Common/importWizard/hooks/useRegimeProfile';
import { selectedLinesPayload } from 'modules/Common/utils/assignImports';
import { assignImports, invalidateImportQueries } from 'modules/Common/utils/assignImports.api';
import CgDialog from './CgDialog';
import CgRadixButton from './CgRadixButton';
import AssignImportsStep from './AssignImportsStep';

// The upload route returns inserted documents, whose Decimal128 values serialise as
// `{ $numberDecimal: "1.5" }` (same handling as CgEmissionFactorHoverText).
const decimalValue = (value) => Number(value?.$numberDecimal ?? value ?? 0);

/**
 * The "Assign installation to imports" step after a template upload (backend
 * docs/features/import-installation-backfill, D14): one section per created factor
 * that covers supplier-only imports (`findAssignSections`). Skip or Assign both end
 * in `onDone` — the factors are already saved either way.
 */
export default function UploadAssignImportsDialog({ show, sections, onSectionsChange, onDone }) {
  const { t } = useTranslation('installations');
  const queryClient = useQueryClient();
  const cnGoods = useCnGoods();
  const { profile: regimeProfile } = useRegimeProfile({ enabled: Boolean(show) });
  const [isAssigning, setIsAssigning] = useState(false);

  const handleAssign = async () => {
    setIsAssigning(true);
    let assigned = 0;
    let skipped = 0;
    let kept = 0;
    // One at a time on purpose: two sections can cover lines of the same import, and
    // each assignment saves that import's whole line list — in parallel, one would
    // overwrite the other.
    for (const section of sections) {
      const lines = selectedLinesPayload(section.preview.lines, section.selected);
      if (lines.length === 0) continue;
      try {
        const result = await assignImports(http, section.factor._id, lines);
        assigned += result.assigned;
        skipped += result.skipped;
        kept += result.kept;
      } catch (error) {
        // The factors are saved; only this assignment failed.
        cgToast.error(t('assignImports.title', 'Assign installation to imports'), {
          description: apiErrorMessage(error) || error.message,
        });
      }
    }
    invalidateImportQueries(queryClient);
    setIsAssigning(false);
    if (assigned > 0) {
      notify(
        t('assignImports.title', 'Assign installation to imports'),
        t('assignImports.assignedToast', {
          count: assigned,
          defaultValue: '{{count}} import now uses actual values.',
          defaultValue_other: '{{count}} imports now use actual values.',
        }),
        AlertType.SUCCESS,
      );
    }
    if (kept > 0) {
      notify(
        t('assignImports.keptTitle', 'Choose the product on some imports'),
        t('assignImports.keptBody', {
          count: kept,
          defaultValue:
            "{{count}} import got the installation but keeps its current values: the installation has several products for this CN good and the import doesn't say which.",
          defaultValue_other:
            "{{count}} imports got the installation but keep their current values: the installation has several products for this CN good and the imports don't say which.",
        }),
        AlertType.WARNING,
      );
    }
    if (skipped > 0) {
      notify(
        t('assignImports.skippedTitle', 'Some imports were not assigned'),
        t('assignImports.skippedBody', {
          count: skipped,
          defaultValue: '{{count}} import could not be assigned and keeps its current values.',
          defaultValue_other:
            '{{count}} imports could not be assigned and keep their current values.',
        }),
        AlertType.WARNING,
      );
    }
    onDone();
  };

  const selectedCount = sections.reduce((sum, section) => sum + section.selected.size, 0);
  return (
    <CgDialog
      open={show}
      onOpenChange={(next) => {
        if (!next && !isAssigning) onDone();
      }}
      title={t('assignImports.title', 'Assign installation to imports')}
      width={800}
      closeOnOverlayClick={false}
      footer={
        <div className="flex justify-end gap-2">
          <CgRadixButton
            variant="secondary"
            onClick={onDone}
            disabled={isAssigning}
          >
            {t('assignImports.skip', 'Skip')}
          </CgRadixButton>
          <CgRadixButton
            variant="primary"
            onClick={handleAssign}
            disabled={selectedCount === 0}
            loading={isAssigning}
          >
            {t('assignImports.assign', {
              count: selectedCount,
              defaultValue: 'Assign {{count}} import',
              defaultValue_other: 'Assign {{count}} imports',
            })}
          </CgRadixButton>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {sections.map((section, index) => (
          <AssignImportsStep
            key={section.factor._id}
            idPrefix={`assign-imports-${section.factor._id}`}
            preview={section.preview}
            isUk={regimeProfile?.regime === 'UK'}
            factor={{
              cnGood: section.factor.cnGood,
              cnName: cnGoods?.find?.((good) => good._id === section.factor.cnGood)?.name,
              date: section.factor.date,
              lastRecordDate: section.factor.lastRecordDate,
              direct: decimalValue(section.factor.emissionFactors?.direct),
              indirect: decimalValue(section.factor.emissionFactors?.indirect),
              total: decimalValue(section.factor.emissionFactors?.total),
            }}
            selected={section.selected}
            onSelectionChange={(selected) =>
              onSectionsChange(sections.map((s, i) => (i === index ? { ...s, selected } : s)))
            }
          />
        ))}
      </div>
    </CgDialog>
  );
}

UploadAssignImportsDialog.propTypes = {
  show: PropTypes.bool,
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSectionsChange: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};
